import React from 'react';
import { WorkspaceListRowProps, WorkspaceItem } from './types';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import { useTranslation } from 'react-i18next';
import { selectAllDocuments } from '../../../../slices/documents/documentsSlice';
import { useLibraryFunctions } from '../../../library/hooks/useLibraryFunctions';
import styles from './WorkspaceList.module.scss';
import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CalendarIcon from '../../../../../static/assets/svg/calendar_month.svg';
import { Helpers } from '../../../../common/helpers/helpers';
import PdfIcon from '../../../../../static/assets/svg/pdf_icon.svg';
import DocxIcon from '../../../../../static/assets/svg/docx_icon.svg';
import BKIcon from '../../../../../static/assets/svg/bk_icon.svg';
import TemplateIcon from '../../../../../static/assets/svg/template_icon.svg';
import { useDocumentsFunctions } from '../../../document-vault/hooks/useDocumentsFunctions';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import { showMessage } from '../../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';

export const WorkspaceListRow = ({ item, renderActionButtons }: WorkspaceListRowProps) => {
  const company = useAppSelector(selectAllCompanies)[0];
  const { t } = useTranslation();
  const documentUploads = useAppSelector(selectAllDocuments);
  const dispatch = useAppDispatch();
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;
  const { getFileTypeFromName, navigateToDocumentPageTab } = useDocumentsFunctions();
  const { navigateToTemplatePage } = useLibraryFunctions();
  const handleNavigateTemplate = (item: WorkspaceItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      return false;
    } else {
      if (!('region' in item)) {
        navigateToTemplatePage('company-templates', item.id);
      } else {
        if (freePlan) {
          if (actionAvailableFree) {
            navigateToTemplatePage(item.region, item.id);
          } else {
            dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
          }
        } else {
          navigateToTemplatePage(item.region, item.id);
        }
      }
    }
  };
  const handleNavigate = (item: WorkspaceItem) => {
    if ('du_types' in item || 'dc_types' in item) {
      navigateToDocumentPageTab(item, 0);
    } else {
      handleNavigateTemplate(item);
    }
  };
  const renderProfile = (item: WorkspaceItem) => {
    if ('dl_categories' in item) {
      return item.dl_categories.length > 0 ? item.dl_categories.join(', ') : '-';
    } else if ('category' in item) {
      return item.category;
    } else {
      return '-';
    }
  };

  const renderWorkflow = (item: WorkspaceItem) => {
    if ('dl_workflows' in item) {
      return item.dl_workflows.length > 0 ? item.dl_workflows.join(', ') : '-';
    } else if ('dt_workflows' in item) {
      return item.dt_workflows.length > 0 ? item.dt_workflows.join(', ') : '-';
    } else if ('du_workflows' in item) {
      return item.du_workflows.length > 0 ? item.du_workflows.join(', ') : '-';
    } else if ('dc_workflows' in item) {
      return item.dc_workflows.length > 0 ? item.dc_workflows.join(', ') : '-';
    }
  };

  const renderType = (item: WorkspaceItem) => {
    if ('dl_types' in item) {
      return item.dl_types.length > 0 ? item.dl_types.join(', ') : '-';
    } else if ('dt_types' in item) {
      return item.dt_types.length > 0 ? item.dt_types.join(', ') : '-';
    } else if ('du_types' in item) {
      return item.du_types.length > 0 ? item.du_types.join(', ') : '-';
    } else if ('dc_types' in item) {
      return item.dc_types.length > 0 ? item.dc_types.join(', ') : '-';
    }
  };

  const renderFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return (
          <Tooltip title={t('fileTypes.pdf')} arrow placement='top'>
            <span>
              <PdfIcon />
            </span>
          </Tooltip>
        );
      case 'docx':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'doc':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'bk':
        return (
          <Tooltip title={t('fileTypes.businessKitz')} arrow placement='top'>
            <span>
              <BKIcon />
            </span>
          </Tooltip>
        );
      case 'template':
        return (
          <Tooltip title={t('fileTypes.template')} arrow placement='top'>
            <span>
              <TemplateIcon />
            </span>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const renderFileTypeString = () => {
    if ('du_filetype' in item) {
      return item.du_filetype.length > 0
        ? item.du_filetype[0]
        : getFileTypeFromName(item.name, item.contract_generation);
    } else if ('dl_types' in item || !('dc_types' in item)) {
      return 'template';
    } else {
      return 'bk';
    }
  };

  const fileTypeIcon = renderFileTypeIcon(renderFileTypeString());

  return (
    <TableRow className={styles.table_row}>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        <Box className={styles.profile_container}>
          <Typography variant='body2' className={styles.profile_text}>
            {'name' in item && item.name
              ? item.name
              : 'document_upload' in item && item.document_upload
              ? documentUploads.find((el) => el.id === item.document_upload)?.name
              : t('templatePage.headerDefault')}
          </Typography>
          {item.is_free && <Box className={styles.free_tag}>{t('pricingOptionNames.free')}</Box>}
        </Box>
      </TableCell>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        {fileTypeIcon && <Box className={styles.file_type_icon}>{fileTypeIcon}</Box>}
      </TableCell>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        <Typography
          variant='body2'
          sx={{ textTransform: 'capitalize' }}
          className={styles.cell_text}
        >
          {renderProfile(item)}
        </Typography>
      </TableCell>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        <Typography variant='body2' className={styles.cell_text}>
          {renderWorkflow(item)}
        </Typography>
      </TableCell>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        <Typography variant='body2' className={styles.cell_text}>
          {renderType(item)}
        </Typography>
      </TableCell>
      <TableCell className={styles.table_cell} onClick={() => handleNavigate(item)}>
        <Box className={styles.date_container}>
          <CalendarIcon />
          <Typography variant='body2' className={styles.cell_text}>
            {'last_used' in item && item.last_used
              ? Helpers.timeStampToDateV2(item.last_used)
              : 'updated' in item
              ? Helpers.timeStampToDateV2(item.updated)
              : '2025'}
            {/*2025 - just a placeholder*/}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={styles.table_cell}>{renderActionButtons(item)}</TableCell>
    </TableRow>
  );
};
